import { ChartCard } from "../components/ChartCard.js";
import { BarChart } from "../components/charts/BarChart.js";
import { PresenceTrendChart } from "../components/charts/PresenceTrendChart.js";
import { EventTimestampBlock } from "../components/EventTimestampBlock.js";
import { KpiRow } from "../components/KpiRow.js";
import type {
  OpenRawEventsHandler,
  OpenRawEventsOptions,
  PresenceData,
  PresenceDevice,
} from "../types.js";

const CATEGORIES = [
  { key: "phone_evidence", label: "Phone evidence", fill: "#31c48d" },
  { key: "phone_candidate", label: "Phone candidate", fill: "#5aa2ff" },
  { key: "other_personal_device", label: "Other personal", fill: "#bd9bff" },
  { key: "likely_non_phone", label: "Likely non-phone", fill: "#f9c74f" },
  { key: "unknown", label: "Unknown", fill: "#7f94b5" },
] as const;

function formatUtc(value: string) {
  return new Date(value).toISOString().replace("T", " ").slice(0, 19);
}

function percentage(numerator: number, denominator: number) {
  return denominator ? `${((numerator / denominator) * 100).toFixed(1)}%` : "—";
}

function statusOptions(status: string): OpenRawEventsOptions {
  return {
    eventName: "ble_presence_snapshot",
    gameUrl: "all",
    ...(!["invalid_snapshot", "unsupported_schema"].includes(status)
      ? { propertiesContains: `"status":"${status}"` }
      : {}),
  };
}

function distanceCell(device: PresenceDevice, index: number) {
  const bucket = device.distances?.[index];
  return bucket ? (
    <span>
      {bucket.phoneCount} <span className="text-text-secondary">/ {bucket.totalCount}</span>
    </span>
  ) : (
    "—"
  );
}

export function PresencePanel({
  data,
  onOpenRawEvents,
}: {
  data: PresenceData | null;
  onOpenRawEvents: OpenRawEventsHandler;
}) {
  if (!data || data.summary.scans === 0) {
    return (
      <div className="py-8 text-center text-sm text-text-secondary">
        No BLE snapshots in this range. Check the venue/device filters and that the table has the
        BLE-enabled APK. Select a dev/private Venue or enable Include non-public to inspect its
        data.
      </div>
    );
  }
  const { summary } = data;
  const latestOk = data.devices.filter((device) => device.status === "ok");

  return (
    <>
      <div className="mb-3 rounded-lg border border-border bg-panel-2 p-3 text-xs leading-relaxed text-text-secondary">
        Passive BLE presence is experimental: advertisers are not people or unique visitors.
        “Possible phones” combines phone evidence and phone candidates. Distances use one iPhone
        calibration and are approximate. Fleet and public-venue filters apply; Game does not. Latest
        samples below are from the selected range, not necessarily live.
      </div>
      <KpiRow
        items={[
          {
            name: "Tables reporting",
            value: summary.tables.toLocaleString(),
            sub: "with BLE snapshots in this range",
          },
          {
            name: "Successful scans",
            value: summary.successful.toLocaleString(),
            sub: `${summary.scans.toLocaleString()} received · ${percentage(summary.successful, summary.scans)} successful`,
          },
          {
            name: "Near-table activity (~0–2 m)",
            value: percentage(summary.nearTableMinutes, summary.observedTableMinutes),
            sub: "share of observed table-minutes; not occupancy time",
          },
          {
            name: "Unavailable scans",
            value: (summary.scans - summary.successful).toLocaleString(),
            sub: "excluded from presence percentages",
          },
        ]}
      />
      <div className="mb-3 grid gap-3 xl:grid-cols-3">
        <div className="min-w-0 xl:col-span-2">
          <PresenceTrendChart data={data} onOpenRawEvents={onOpenRawEvents} />
        </div>
        <ChartCard
          title="Latest sample: advertiser categories"
          criteria="Take the latest BLE snapshot per table inside the selected date range, ordered by client_timestamp then received_at/id. Only latest snapshots with status ok and supported v1 counts contribute. SUM each category across its four distance groups, then across those tables. These are advertiser observations, not deduplicated people; one advertiser may be observed by multiple tables. A latest-per-table filter is not available in Raw Events, so this chart has no segment drill-down. Use the table's View scans button."
          note={`${latestOk.length} tables with successful latest samples. Counts across tables can overlap.`}
        >
          {latestOk.length > 0 ? (
            <BarChart
              verbatimAxisLabels
              axisLabelMaxChars={20}
              data={CATEGORIES.map((category) => ({
                label: category.label,
                fill: category.fill,
                value: latestOk.reduce(
                  (total, device) => total + (device.categories?.[category.key] ?? 0),
                  0,
                ),
              }))}
              barTooltip={(item) =>
                `${item.label}: ${item.value} advertiser observations from latest successful table samples. No exact Raw Events filter for this aggregate; use View scans below.`
              }
            />
          ) : (
            <p className="py-5 text-sm text-text-secondary">
              Latest samples are unavailable; category counts were not observed.
            </p>
          )}
        </ChartCard>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-text-secondary">Scan status:</span>
        {data.statuses.map((row) => (
          <button
            key={row.status}
            className="rounded border border-border px-2 py-1 hover:bg-panel-2"
            style={{ color: row.status === "ok" ? "#31c48d" : "#f9c74f" }}
            title={
              ["invalid_snapshot", "unsupported_schema"].includes(row.status)
                ? "Raw Events → all BLE scans (derived status has no exact raw filter)"
                : `Raw Events → ble_presence_snapshot with reported status ${row.status}`
            }
            onClick={(event) => onOpenRawEvents(statusOptions(row.status), event)}
            onAuxClick={(event) => {
              if (event.button === 1) {
                event.preventDefault();
                onOpenRawEvents(statusOptions(row.status), event);
              }
            }}
          >
            {row.status}: {row.count.toLocaleString()}
          </button>
        ))}
      </div>
      <ChartCard
        title="Latest sample per table"
        criteria="One latest BLE snapshot per table in the selected date range, including unavailable scans. Possible phone counts are phone_evidence + phone_candidate; the denominator in each distance cell is all advertisers in that distance group. Window % = successful table-minutes with a possible phone below 2 m / all successful table-minutes for that table. Unavailable counts are shown as —, never zero. View scans applies the table ID, date range and BLE event name in Raw Events."
        note={`Most recent ingestion in range: ${summary.lastReceivedAt ? formatUtc(summary.lastReceivedAt) : "—"}. Sample times use the dashboard timezone with UTC subtext.`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-border text-text-secondary">
              <tr>
                {[
                  "Table / venue",
                  "Last sample (local)",
                  "Status",
                  "0–1 m",
                  "1–2 m",
                  "2 m+",
                  "Unknown distance",
                  "All advertisers",
                  "Within ~2 m / window",
                  "",
                ].map((heading) => (
                  <th key={heading} className="whitespace-nowrap px-2 py-2 font-medium">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.devices.map((device) => (
                <tr key={device.id} className="border-b border-border align-top">
                  <td className="px-2 py-3">
                    <div className="font-semibold">{device.name || device.id}</div>
                    <div className="mt-1 text-text-secondary">
                      {device.venueName ?? "Unassigned venue"}
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-text-secondary">
                      {device.id}
                    </div>
                  </td>
                  <td
                    className="whitespace-nowrap px-2 py-3"
                    title={`Received ${device.receivedAt}`}
                  >
                    <EventTimestampBlock
                      clientTimestamp={device.sampleAt}
                      receivedAt={device.receivedAt}
                      compact
                    />
                  </td>
                  <td
                    className="px-2 py-3"
                    style={{ color: device.status === "ok" ? "#31c48d" : "#f9c74f" }}
                    title={`${device.classifierVersion ?? "Unknown classifier"} · ${device.distanceProfile ?? "Unknown calibration"}`}
                  >
                    {device.status}
                  </td>
                  {[0, 1, 2, 3].map((index) => (
                    <td key={index} className="px-2 py-3" title="Possible phones / all advertisers">
                      {distanceCell(device, index)}
                    </td>
                  ))}
                  <td className="px-2 py-3">{device.totalAdvertisers ?? "—"}</td>
                  <td className="px-2 py-3">
                    {percentage(device.nearPresence, device.successful)}
                    <div className="mt-1 text-text-secondary">
                      {device.nearPresence} / {device.successful} observed table-minutes
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <button
                      className="whitespace-nowrap rounded border border-border-subtle px-2 py-1 text-accent hover:bg-panel-2"
                      onClick={(event) =>
                        onOpenRawEvents(
                          {
                            eventName: "ble_presence_snapshot",
                            deviceId: device.id,
                            gameUrl: "all",
                          },
                          event,
                        )
                      }
                      onAuxClick={(event) => {
                        if (event.button === 1) {
                          event.preventDefault();
                          onOpenRawEvents(
                            {
                              eventName: "ble_presence_snapshot",
                              deviceId: device.id,
                              gameUrl: "all",
                            },
                            event,
                          );
                        }
                      }}
                    >
                      View scans
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-text-secondary">
          Distance cells show <strong>possible phones / all advertisers</strong>. Unknown distance
          is separate from unknown device type.
        </p>
      </ChartCard>
    </>
  );
}
